import React from "react";
import { Box, TextField, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import type { FieldSelectedSections } from "@mui/x-date-pickers/models";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { composerEventDetailFieldSx } from "./composerBorderStyles";
import { EVENT_DATE_PICKER_FORMAT, EVENT_DATE_PICKER_LOCALE } from "../../utils/eventDateDisplay";

export type EventDateTimeField =
  "eventStartDate" | "eventStartTime" | "eventEndDate" | "eventEndTime";

export type EventDateTimeFieldsProps = {
  startDate: Date | null;
  startTime: string;
  endDate: Date | null;
  endTime: string;
  errors: Partial<Record<EventDateTimeField, string>>;
  onChange: (field: EventDateTimeField, value: Date | null | string) => void;
  disabled?: boolean;
};

const pickerPopperSlot = {
  sx: { zIndex: (theme: { zIndex: { modal: number } }) => theme.zIndex.modal + 4 },
};

const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

/** "Saturday", for under a date, so a date that lands on a weekend is noticed. */
export const eventWeekday = (date: Date | null): { text: string; weekend: boolean } | null => {
  if (!date || Number.isNaN(date.getTime())) return null;
  const day = date.getDay();
  return {
    text: date.toLocaleDateString("en-GB", { weekday: "long" }),
    weekend: day === 0 || day === 6,
  };
};

type EventFieldProps = {
  name: EventDateTimeField;
  /** Shown above the field; without it (e.g. under a table heading), ariaLabel names it. */
  label?: string;
  ariaLabel?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  /**
   * Keep a line under the field even when there's nothing to say, so a problem
   * appearing doesn't move the fields below (the Planner's table rows).
   */
  reserveHelperLine?: boolean;
};

/** The text under a field: its problem, else (for a date) its weekday, else an empty line if one is kept. */
const helperFor = (
  error: string | undefined,
  reserveHelperLine: boolean | undefined,
  weekday?: ReturnType<typeof eventWeekday>,
): React.ReactNode => {
  if (error) return error;
  if (weekday) {
    return (
      <Box component="span" sx={{ color: weekday.weekend ? "warning.dark" : "text.secondary" }}>
        {weekday.weekend ? `${weekday.text} (weekend)` : weekday.text}
      </Box>
    );
  }
  return reserveHelperLine ? " " : undefined;
};

/** The field shows DD/MM/YYYY with no digits typed yet. */
const isEmptyDateText = (text: string) => !/\d/.test(text);

/**
 * One of an Event's dates, as Add Event picks it, with its weekday under it.
 * Clicking anywhere in an empty date starts typing at the day, so typing
 * 13022027 fills the whole date wherever the click landed.
 */
export const EventDateField: React.FC<
  EventFieldProps & {
    value: Date | null;
    minDate?: Date;
    onChange: (date: Date | null) => void;
  }
> = ({
  name,
  label,
  ariaLabel,
  required,
  error,
  disabled,
  value,
  minDate,
  onChange,
  reserveHelperLine,
}) => {
  const [selectedSections, setSelectedSections] = React.useState<FieldSelectedSections>(null);
  // Set by a click on an empty date, so the section the click picked becomes the day instead.
  const clickedEmpty = React.useRef(false);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={EVENT_DATE_PICKER_LOCALE}>
      <DatePicker
        label={label}
        format={EVENT_DATE_PICKER_FORMAT}
        value={value}
        minDate={minDate}
        disabled={disabled}
        enableAccessibleFieldDOMStructure={false}
        onChange={onChange}
        selectedSections={selectedSections}
        onSelectedSectionsChange={(sections) => {
          if (clickedEmpty.current && sections !== null) {
            clickedEmpty.current = false;
            setSelectedSections(0);
            return;
          }
          setSelectedSections(sections);
        }}
        slotProps={{
          textField: {
            name,
            fullWidth: true,
            size: "small",
            required,
            sx: composerEventDetailFieldSx,
            error: !!error,
            helperText: helperFor(error, reserveHelperLine, eventWeekday(value)),
            inputProps: ariaLabel ? { "aria-label": ariaLabel } : undefined,
            onMouseDown: (event: React.MouseEvent<HTMLDivElement>) => {
              const target = event.target as HTMLElement;
              clickedEmpty.current =
                target instanceof HTMLInputElement && isEmptyDateText(target.value);
            },
          },
          popper: pickerPopperSlot,
        }}
      />
    </LocalizationProvider>
  );
};

/** Room at the right of a time box for its clock button, which should still open the picker. */
const TIME_PICKER_BUTTON_WIDTH = 32;

/**
 * Clicking an empty time box starts typing at the hour wherever the click
 * landed, so typing 0900 gives 09:00 rather than filling the minutes.
 */
const startEmptyTimeAtHour = (event: React.MouseEvent<HTMLInputElement>) => {
  const input = event.currentTarget;
  if (input.value) return;
  const { right } = input.getBoundingClientRect();
  if (event.clientX > right - TIME_PICKER_BUTTON_WIDTH) return;
  event.preventDefault();
  input.focus();
};

/** One of an Event's times, as Add Event takes it. */
export const EventTimeField: React.FC<
  EventFieldProps & {
    value: string;
    onChange: (time: string) => void;
  }
> = ({ name, label, ariaLabel, required, error, disabled, value, onChange, reserveHelperLine }) => (
  <TextField
    fullWidth
    size="small"
    variant="outlined"
    name={name}
    type="time"
    required={required}
    disabled={disabled}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    label={label}
    error={!!error}
    helperText={helperFor(error, reserveHelperLine)}
    sx={composerEventDetailFieldSx}
    InputLabelProps={label ? { shrink: true } : undefined}
    inputProps={{
      ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
      onMouseDown: startEmptyTimeAtHour,
    }}
  />
);

/**
 * A date beside its time. The date gets more of the room so its year shows on a
 * phone; on a very narrow screen the time goes under it.
 */
const DATE_TIME_ROW_SX = {
  display: "flex",
  flexWrap: "wrap",
  gap: 1.5,
  "& > :first-of-type": { flex: "3 1 170px" },
  "& > :last-of-type": { flex: "2 1 110px" },
} as const;

/** An Event's start date and time, and optional end: Add Event's fields, also used on Planner rows. */
const EventDateTimeFields: React.FC<EventDateTimeFieldsProps> = ({
  startDate,
  startTime,
  endDate,
  endTime,
  errors,
  onChange,
  disabled = false,
}) => {
  const today = React.useMemo(startOfToday, []);

  return (
    <>
      <Box sx={{ ...DATE_TIME_ROW_SX, mb: 2, mt: { xs: 1.5, sm: 2 } }}>
        <EventDateField
          name="eventStartDate"
          label="Start date"
          required
          value={startDate}
          minDate={today}
          error={errors.eventStartDate}
          disabled={disabled}
          onChange={(date) => onChange("eventStartDate", date)}
        />
        <EventTimeField
          name="eventStartTime"
          label="Start time"
          required
          value={startTime}
          error={errors.eventStartTime}
          disabled={disabled}
          onChange={(time) => onChange("eventStartTime", time)}
        />
      </Box>

      <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, mb: 1 }}>
        End (optional)
      </Typography>
      <Box sx={{ ...DATE_TIME_ROW_SX, mb: 2 }}>
        <EventDateField
          name="eventEndDate"
          label="End date"
          value={endDate}
          minDate={startDate ?? undefined}
          error={errors.eventEndDate}
          disabled={disabled}
          onChange={(date) => onChange("eventEndDate", date)}
        />
        <EventTimeField
          name="eventEndTime"
          label="End time"
          value={endTime}
          error={errors.eventEndTime}
          disabled={disabled}
          onChange={(time) => onChange("eventEndTime", time)}
        />
      </Box>
    </>
  );
};

export { startOfToday };
export default EventDateTimeFields;
